import { Box, HStack, Link, Text } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import type { ParentTicket } from '@/types/parentTicket'
import { StatusPill } from '@/components/helpdesk/Cell/StatusPill'

type Props = {
  data: ParentTicket[]
  height?: string | number
  /** When set, row click navigates to this path (e.g. edit page). Default: detail page */
  getRowPath?: (row: ParentTicket) => string
}

const COLS =
  'minmax(110px, 0.8fr) \
   minmax(120px, 0.8fr) \
   minmax(100px, 1fr) \
   minmax(120px, 0.9fr) \
   minmax(80px, 1.3fr) \
   minmax(90px, 0.6fr) \
   minmax(100px, 0.7fr) \
   minmax(190px, 1fr)'

const MIN_TABLE_W = '1120px'

function formatDateTime(iso: string) {
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function ParentTicketTable({ data, height = '100%', getRowPath }: Props) {
  const navigate = useNavigate()
  const pathFor = (r: ParentTicket) =>
    getRowPath ? getRowPath(r) : `/tickets/${r.id}`

  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      overflow="hidden"
      h={height}
      minW={0}
      display="flex"
      flexDirection="column"
    >
      <Box flex="1" minH={0} overflowX="auto" overflowY="hidden" minW={0}>
        <Box minW={MIN_TABLE_W} h="100%">
          <Box maxH="100%" overflowY="auto" sx={{ scrollBehavior: 'smooth' }}>
            <Box
              position="sticky"
              top={0}
              zIndex={2}
              display="grid"
              gridTemplateColumns={COLS}
              px={4}
              py={3}
              bg="white"
              fontSize="sm"
              fontWeight="semibold"
              borderBottom="1px solid"
              borderColor="gray.200"
            >
              <Box>Ticket ID</Box>
              <Box>Request ID</Box>
              <Box>Client</Box>
              <Box>Agent</Box>
              <Box>Date and Time of Request</Box>
              <Box>Ageing</Box>
              <Box>Severity</Box>
              <Box>Status</Box>
            </Box>

            {data.map((r) => (
              <Box
                key={r.id}
                role="button"
                tabIndex={0}
                display="grid"
                gridTemplateColumns={COLS}
                px={4}
                py={3}
                borderBottom="1px solid"
                borderColor="gray.100"
                fontSize="sm"
                alignItems="center"
                cursor="pointer"
                _hover={{ bg: 'gray.50' }}
                onClick={() => navigate(pathFor(r))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    navigate(pathFor(r))
                  }
                }}
              >
                <Box>
                  <Text fontWeight="semibold">{r.id}</Text>
                </Box>

                <Box>
                  {/* prevent row click when clicking the link */}
                  <Link
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(pathFor(r))
                    }}
                    color="blue.600"
                    fontWeight="semibold"
                    textDecoration="underline"
                  >
                    {r.requestId}
                  </Link>
                </Box>

                <Box minW={0}>
                  <Text fontWeight="semibold" noOfLines={1}>
                    {r.clientName}
                  </Text>
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    {r.clientOrg ?? '-'}
                  </Text>
                </Box>

                <Box minW={0}>
                  {r.agentName ? (
                    <Text fontWeight="semibold" noOfLines={1}>
                      {r.agentName}
                    </Text>
                  ) : (
                    <HStack spacing={2} onClick={(e) => e.stopPropagation()}>
                      <Text fontWeight="bold" color="gray.500">
                        +
                      </Text>
                      <Link
                        color="blue.600"
                        fontWeight="semibold"
                        textDecoration="underline"
                      >
                        Assign Agent
                      </Link>
                    </HStack>
                  )}
                </Box>

                <Box minW={0} whiteSpace="nowrap">
                  {formatDateTime(r.requestedAt)}
                </Box>

                <Box>{r.ageingLabel}</Box>

                <Box>
                  <StatusPill label={r.severity} variant="severity" />
                </Box>

                <Box>
                  <StatusPill label={r.status} variant="status" />
                </Box>
              </Box>
            ))}

            {data.length === 0 && (
              <Box px={4} py={8} textAlign="center" color="gray.500">
                No Record Found
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
